import {
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
  Button,
  useRecordContext,
  BooleanInput,
  TopToolbar,
  useDataProvider,
  useNotify,
  ReferenceInput,
  SelectInput,
  FileInput,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import React from "react";
import ColorInput from "../../components/color/color-input";
import ImageInput from "../../components/image/image-input";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from "@mui/material";
import { 
  Download as DownloadIcon,
  Build as BuildIcon,
  Code as CodeIcon,
  FolderOpen as FolderOpenIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Javascript as JavaScriptIcon,
  Html as HtmlIcon,
  Css as CssIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  CheckCircle as ValidateIcon,
  CloudUpload as PublishIcon,
} from "@mui/icons-material";
import ArrayEditor from "../../components/array/array.editor";
import AppModuleEdit from "./AppModuleEdit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AppFormEdit from "./AppFormEdit";
import ConfigurationEdit from "./AppConfigurationEdit";
import AIFunctionEdit from "../ai/assistant/AIFunctionEdit";
import ApplicationApiDocs from "../../components/api/ApplicationApiDocs";
import { getConfig } from "../../framework/config/app.config";
import { useState, useRef, useEffect } from "react";
import ChatInterface from "../ai/chat/ChatInterface";
import EmbedAgent from "../ai/embed/EmbedAgent";
import { AgenticFeatureRegistry } from "../ai/chat/AgenticFeatureRegistry";
import { useAgenticContext } from "../ai/chat/hooks/useAgenticContext";
import SavedMockConfigurations from "./SavedMockConfigurations";
import MockConfigurationControl from "./MockConfigurationControl";
import { getBaseUrl } from "../../framework/config/api.config";
import CodeEditor from "../../components/code/CodeEditor";
import LivePreviewFrame from "../../components/live-preview/LivePreviewFrame";
import TemplateEditorAce from "../../components/code/ace/template.editor";
import { apiClient } from "../../framework/api/api.client";
import { Alert, Tabs, Tab } from "@mui/material";
import { TabbedFormTabs } from "react-admin";



// Custom scrollable tabs component for TabbedForm
const ScrollableTabbedFormTabs = (props: any) => (
  <TabbedFormTabs 
    {...props} 
    variant="scrollable" 
    scrollButtons="auto"
    sx={{
      '& .MuiTabs-scrollButtons': {
        '&.Mui-disabled': {
          opacity: 0.3,
        }
      }
    }}
  />
);

const ApplicationKeyWrapper = ({ children }) => {
  const record = useRecordContext();

  //console.log('record', record);

  if (!record) return null; // or a loading spinner

  return children(record.key);
};

const AppPreviewFrame = () => {
  const record = useRecordContext();

  const getAppUrl = (app) => {
    console.log("app =>", app);
    console.log("app.configuration.configurationValues =>", app?.configuration?.configurationValues);

    if (!app) {
      return 'about:blank';
    }

    if (app.key === 'CAKE.APPLICATION.KEY') {
      return 'http://localhost:8080';
    } else if (app.key === 'CI.APPLICATION.KEY') {
     // return 'http://localhost:3001';
     const baseApiUrl = getBaseUrl() + '/cai';
     return baseApiUrl;
    } else if (app.key?.toLowerCase() === 'concierge') {
      return 'http://localhost:3000';
    }
    else if (app?.configuration?.configurationValues?.appPreview?.url) {
      return app.configuration.configurationValues.appPreview.url;
    }
  
      
    return 'about:blank';
  };

  const key = record?.key;
  const url = record ? getAppUrl(record) : 'about:blank';

  return (
    <div style={{ height: "900px", width: "100%", marginLeft: "10px" }}>
      <iframe
        src={url}
        width="100%"
        height="100%"
        allow="fullscreen; display-capture; clipboard-write"
        title="App Preview"
      ></iframe>
    </div>
  );
};

const CompilationStatus = () => {
  const record = useRecordContext();
  
  if (!record?.configuration) return null;

  const isCompiled = record.configuration.isUICompiled;
  const hasUI = record.configuration.hasUI;
  const compiledAt = record.configuration.uiCompiledAt;
  const assetManifest = record.configuration.assetManifest;

  if (!hasUI) return null;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          UI Build Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip 
            label={hasUI ? "Has UI" : "No UI"} 
            color={hasUI ? "success" : "default"}
            variant={hasUI ? "filled" : "outlined"}
          />
          <Chip 
            label={isCompiled ? "Compiled" : "Not Compiled"} 
            color={isCompiled ? "success" : "default"}
            variant={isCompiled ? "filled" : "outlined"}
          />
        </Box>
        
        {compiledAt && (
          <Typography variant="body2" color="textSecondary">
            Last compiled: {new Date(compiledAt).toLocaleString()}
          </Typography>
        )}
        
        {assetManifest && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Build version: {assetManifest.version} | 
              Size: {Math.round((assetManifest.buildSize || 0) / 1024)}KB |
              Files: {Object.keys(assetManifest.files || {}).length}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const LiveWASMPreview = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [sourceBundle, setSourceBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [selectedFile, setSelectedFile] = useState(null);
  const [modifiedFiles, setModifiedFiles] = useState({});
  const [compiledApp, setCompiledApp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // 🔌 Register UI Builder feature with AI chat when this tab is active
  useEffect(() => {
    if (record?.key) {
      console.log(`🎯 Registering UI Builder feature for application: ${record.name} (${record.key})`);
      
      // Register a temporary provider for this application's context
      const applicationProvider = {
        id: `application-ui-builder-${record.id}`,
        name: `Application UI Builder (${record.name})`,
        getPromptForFocus: (focusKey: string) => {
          if (focusKey === `application:${record.id}`) {
            return `I'm now working on the "${record.name}" application (ID: ${record.id}). This is a React application${record.configuration?.hasUI ? ' with source code available for editing' : ''}. The application key is "${record.key}".

🚨 CRITICAL WORKFLOW REQUIREMENTS:
1. When building/modifying UI for this app, create a session with initializeUIProject()
2. Add/update files as needed
3. Call validateUIProject() to check the project
4. **IMMEDIATELY after validation passes**, call finalizeUIProject() with these EXACT parameters:
   - sessionId: (your session ID)
   - applicationId: "${record.id}" ← THIS IS REQUIRED to update this app instead of creating new one!

⚠️ DO NOT end the conversation without calling finalizeUIProject() or all work will be lost!`;
          }
          return null;
        }
      };
      
      AgenticFeatureRegistry.register(applicationProvider);
    }

    // Cleanup provider registration
    return () => {
      console.log('🧹 Clearing UI Builder feature registration');
      if (record?.id) {
        AgenticFeatureRegistry.unregister(`application-ui-builder-${record.id}`);
      }
    };
  }, [record?.id, record?.key, record?.name, record?.configuration?.hasUI]);
  
  // Note: Feature context (featureKey: 'application-ui-builder') is now managed by route-based context in App.tsx
  // This component only manages the focusKey dynamically based on the record
  useAgenticContext({
    focusKey: record?.id ? `application:${record.id}` : undefined,
    clearOnUnmount: true,
  });

  const loadSourceBundle = async () => {
    if (!record?.id) {
      console.error('No record ID available');
      return;
    }

    setLoading(true);
    console.log('🔄 Loading source bundle for Application:', record);
    
    try {
      let bundle;
      
      if (record.sourceCodeStoreId) {
        console.log('📡 Trying Application source bundle via API client');
        try {
          bundle = await apiClient.get(`/api/application/${record.id}/source-bundle`);
        } catch (error) {
          console.log('📡 Application source bundle failed:', error.message);
        }
      }
      
      if (!bundle) {
        throw new Error('No source bundle could be loaded');
      }
      
      console.log('✅ Source bundle loaded:', {
        files: Object.keys(bundle.files || {}).length,
        dependencies: Object.keys(bundle.dependencies || {}).length,
        config: bundle.config
      });
      
      setSourceBundle(bundle);
      
      // Auto-select the entry point file
      if (bundle.config?.entryPoint && bundle.files[bundle.config.entryPoint]) {
        setSelectedFile(bundle.config.entryPoint);
      } else {
        // Fallback to first file
        const firstFile = Object.keys(bundle.files)[0];
        setSelectedFile(firstFile);
      }
      
    } catch (error) {
      console.error('❌ Failed to load source bundle:', error);
      alert(`Failed to load source bundle: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const compileAndPreview = async () => {
    if (!sourceBundle) return;
    
    try {
      console.log('🔄 Compiling current bundle...');
      
      // If there are unsaved modifications, include them in compilation
      const finalFiles = Object.keys(modifiedFiles).length > 0 
        ? { ...sourceBundle.files, ...modifiedFiles }
        : sourceBundle.files;
      
      const bundleWithChanges = { ...sourceBundle, files: finalFiles };
      
      setCompiledApp(bundleWithChanges);
      setActiveTab('preview'); // Switch to preview tab
      
    } catch (error) {
      console.error('❌ Compilation failed:', error);
      alert(`Compilation failed: ${error.message}`);
    }
  };

  const saveModifications = async () => {
    if (!modifiedFiles || Object.keys(modifiedFiles).length === 0) {
      console.log('No modifications to save');
      return;
    }

    setSaving(true);
    try {
      console.log('💾 Updating in-memory bundle with modifications...', Object.keys(modifiedFiles));
      
      // Update the source bundle in memory with modified files
      const updatedBundle = {
        ...sourceBundle,
        files: {
          ...sourceBundle.files,
          ...modifiedFiles
        }
      };
      
      // Update the bundle state
      setSourceBundle(updatedBundle);
      
      // Clear the modified files tracking since they're now "saved" to the bundle
      setModifiedFiles({});
      
      // If we're currently viewing the preview, recompile with the updated bundle
      if (activeTab === 'preview') {
        console.log('🔄 Auto-recompiling preview with updated bundle...');
        try {
          // Merge original files with modifications and compile
          const bundleWithChanges = { ...updatedBundle };
          setCompiledApp(bundleWithChanges);
        } catch (error) {
          console.error('❌ Auto-recompilation failed:', error);
        }
      }
      
      console.log('✅ Updated in-memory bundle with', Object.keys(modifiedFiles).length, 'modified files');
      
    } catch (error) {
      console.error('❌ Failed to update bundle:', error);
    } finally {
      setSaving(false);
    }
  };

  const publishToServer = async () => {
    if (!sourceBundle) {
      console.log('No source bundle to publish');
      return;
    }

    setPublishing(true);
    try {
      console.log('🚀 Publishing source bundle to server...');
      
      // TODO: Implement API call to save the entire updated bundle back to the server
      // This would update the UIFileStore with all the current file contents
      // Example API call:
      // await apiClient.put(`/api/application/${record.id}/source-bundle`, {
      //   files: sourceBundle.files,
      //   dependencies: sourceBundle.dependencies,
      //   config: sourceBundle.config
      // });
      
      console.log('✅ Published source bundle to server');
      
      // Show success notification
      // notify('Source bundle published successfully!', { type: 'success' });
      
    } catch (error) {
      console.error('❌ Failed to publish to server:', error);
      // notify('Failed to publish source bundle', { type: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Box>
      {/* Header with Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Live Preview
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Edit your source code directly in the browser with live compilation and preview.
          </Typography>
        </Box>
        
        
        {/* Editor toolbar buttons */}
        {sourceBundle && activeTab === 'editor' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Save to bundle button - only show when there are changes */}
            {Object.keys(modifiedFiles).length > 0 && (
              <IconButton
                onClick={saveModifications}
                disabled={saving}
                title={saving ? 'Saving...' : `Save ${Object.keys(modifiedFiles).length} Changes to Bundle`}
                color="success"
                size="small"
                sx={{ position: 'relative' }}
              >
                <SaveIcon />
                <Box sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  bgcolor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  {Object.keys(modifiedFiles).length}
                </Box>
              </IconButton>
            )}
            
            {/* Publish to server button */}
            <IconButton
              onClick={publishToServer}
              disabled={publishing}
              title={publishing ? 'Publishing...' : 'Publish Bundle to Server'}
              color="primary"
              size="small"
            >
              {publishing ? (
                <CircularProgress size={16} sx={{ color: 'primary.main' }} />
              ) : (
                <PublishIcon />
              )}
            </IconButton>
          </Box>
        )}
      </Box>

      {sourceBundle && (
        <Box sx={{ width: '100%', minWidth: '1200px' }}>
          {/* View Switcher - Small icons in upper right */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            mb: 0.5,
            position: 'relative',
            zIndex: 10
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5, 
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5
            }}>
              <IconButton
                onClick={() => setActiveTab('editor')}
                size="small"
                title="Code Editor"
                sx={{ 
                  bgcolor: activeTab === 'editor' ? '#333' : 'transparent',
                  color: activeTab === 'editor' ? 'white' : 'text.primary',
                  '&:hover': { 
                    bgcolor: activeTab === 'editor' ? '#444' : 'action.hover',
                    color: activeTab === 'editor' ? 'white' : 'text.primary'
                  }
                }}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => {
                  // Auto-compile when switching to preview if we have changes or no compiled app
                  if (Object.keys(modifiedFiles).length > 0 || !compiledApp) {
                    compileAndPreview();
                  }
                  setActiveTab('preview');
                }}
                size="small"
                title="Preview"
                sx={{ 
                  bgcolor: activeTab === 'preview' ? '#333' : 'transparent',
                  color: activeTab === 'preview' ? 'white' : 'text.primary',
                  '&:hover': { 
                    bgcolor: activeTab === 'preview' ? '#444' : 'action.hover',
                    color: activeTab === 'preview' ? 'white' : 'text.primary'
                  }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Tab Content - Force full width with minimum */}
          <Box sx={{ 
            width: '100%', 
            minWidth: '1200px',
            height: '70vh', 
            minHeight: '600px',
            overflow: 'hidden' // Prevent content from affecting parent width
          }}>
            {activeTab === 'preview' && (
              <Box sx={{ width: '100%', minWidth: '1200px', height: '100%' }}>
                <PreviewPane compiledApp={compiledApp} sourceBundle={sourceBundle} />
              </Box>
            )}
            
            {activeTab === 'editor' && (
              <Box sx={{ width: '100%', minWidth: '1200px', height: '100%' }}>
                <CodeEditorPane 
                  sourceBundle={sourceBundle}
                  selectedFile={selectedFile}
                  onFileSelect={setSelectedFile}
                  modifiedFiles={modifiedFiles}
                  onFileChange={setModifiedFiles}
                  activeTab={activeTab}
                  compileAndPreview={compileAndPreview}
                  setCompiledApp={setCompiledApp}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
      
      {!sourceBundle && !loading && (
        <Box 
          onClick={loadSourceBundle}
          sx={{ 
            textAlign: 'center', 
            py: 3, 
            bgcolor: '#2d3748', 
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              bgcolor: '#374151',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.4)'
            }
          }}
        >
          <RefreshIcon sx={{ color: '#4ade80', fontSize: 48, mb: 1 }} />
          <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 0.5 }}>
            Load Source Bundle
          </Typography>
          <Typography variant="body2" sx={{ color: '#a0aec0' }}>
            Click to load source code from server
          </Typography>
        </Box>
      )}
      
      {loading && (
        <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#2d3748', borderRadius: 1 }}>
          <CircularProgress size={32} sx={{ color: '#e2e8f0', mb: 1 }} />
          <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
            Loading source bundle...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Preview Pane Component
const PreviewPane = ({ compiledApp, sourceBundle }) => {
  if (compiledApp) {
    return (
      <Box sx={{ width: '100%', minWidth: '1200px', height: '100%' }}>
        <LivePreviewFrame 
          sourceBundle={compiledApp}
          onCompilationResult={(result) => console.log('Compilation result:', result)}
        />
      </Box>
    );
  }
  
  if (sourceBundle) {
    // get the reference to the AIChat and set the feature id to 
    
  return (
    <Box sx={{ width: '100%', minWidth: '1200px', height: '100%', textAlign: 'center', py: 4, bgcolor: '#2d3748', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <Typography variant="body1" sx={{ color: '#e2e8f0' }}>
        Switch to the Code Editor to make changes, then return here to see your preview
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: '#a0aec0' }}>
        Source bundle loaded with {Object.keys(sourceBundle.files).length} files
      </Typography>
    </Box>
  );
  }
  
  return (
    <Box sx={{ width: '100%', height: '100%', textAlign: 'center', py: 4, bgcolor: '#2d3748', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
        Load source bundle to enable preview
      </Typography>
    </Box>
  );
};

// Code Editor Pane Component
const CodeEditorPane = ({ sourceBundle, selectedFile, onFileSelect, modifiedFiles, onFileChange, activeTab, compileAndPreview, setCompiledApp }) => {
  console.log('🎯 CodeEditorPane render:', { selectedFile, filesCount: Object.keys(sourceBundle?.files || {}).length });
  
  const handleFileChange = (filename, content) => {
    console.log('📝 File content changed:', filename, content?.length, 'characters');
    const newModifiedFiles = { ...modifiedFiles, [filename]: content };
    onFileChange(newModifiedFiles);
  };

  const getFileContent = (filename) => {
    if (!filename) return '';
    // Return modified version if it exists, otherwise original
    const content = modifiedFiles[filename] || sourceBundle.files[filename] || '';
    console.log('📄 Getting content for:', filename, 'length:', content.length);
    return content;
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {/* File Tree */}
      <FileTreeNavigator 
        files={sourceBundle?.files || {}}
        selectedFile={selectedFile}
        onFileSelect={onFileSelect}
        modifiedFiles={modifiedFiles}
      />
      
      {/* Editor */}
      <SourceFileEditor
        filename={selectedFile}
        content={getFileContent(selectedFile)}
        onContentChange={(content) => handleFileChange(selectedFile, content)}
        sourceBundle={sourceBundle}
        modifiedFiles={modifiedFiles}
        onFileChange={onFileChange}
        activeTab={activeTab}
        compileAndPreview={compileAndPreview}
        setCompiledApp={setCompiledApp}
      />
    </Box>
  );
};

// File Tree Navigator Component
const FileTreeNavigator = ({ files, selectedFile, onFileSelect, modifiedFiles }) => {
  const fileTree = buildFileTree(files);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['src'])); // Start with 'src' expanded
  
  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  };
  
  const renderTreeNode = (node, path = '', depth = 0) => {
    if (node.isFile) {
      const isModified = modifiedFiles[node.path];
      const isSelected = selectedFile === node.path;
      
      return (
        <Box
          key={node.path}
          sx={{
            py: 0.5,
            px: 1,
            pl: 1 + (depth * 1.5), // Indent based on depth
            cursor: 'pointer',
            bgcolor: isSelected ? '#4299e1' : 'transparent',
            color: isSelected ? 'white' : '#e2e8f0',
            '&:hover': {
              bgcolor: isSelected ? '#4299e1' : 'rgba(255,255,255,0.1)'
            },
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={() => {
            console.log('🖱️ File clicked:', node.path);
            onFileSelect(node.path);
          }}
        >
          <Box sx={{ marginRight: '8px', color: 'white', display: 'flex', alignItems: 'center' }}>
            {getFileIcon(node.name)}
          </Box>
          <span style={{ color: isModified ? '#ff9800' : 'inherit', fontWeight: isModified ? 'bold' : 'normal' }}>
            {node.name}
          </span>
          {isModified && <span style={{ marginLeft: '4px', color: '#ff9800' }}>●</span>}
        </Box>
      );
    }
    
    const folderPath = path + node.name;
    const isExpanded = expandedFolders.has(folderPath) || expandedFolders.has(node.name);
    
    return (
      <Box key={folderPath}>
        <Box
          sx={{ 
            fontWeight: 'bold', 
            py: 0.5, 
            px: 1,
            pl: 1 + (depth * 1.5), // Indent based on depth
            bgcolor: 'rgba(255,255,255,0.05)',
            color: '#a0aec0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
          onClick={() => toggleFolder(node.name)}
        >
          <Box sx={{ marginRight: '8px', color: 'white', display: 'flex', alignItems: 'center' }}>
            {isExpanded ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
          </Box>
          <span>{node.name}/</span>
        </Box>
        {isExpanded && Object.values(node.children)
          .sort((a: any, b: any) => {
            // Folders first, then files
            if (a.isFile && !b.isFile) return 1;
            if (!a.isFile && b.isFile) return -1;
            // Within same type, sort alphabetically
            return a.name.localeCompare(b.name);
          })
          .map(child => renderTreeNode(child, folderPath + '/', depth + 1))}
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: '250px', 
      borderRight: 1, 
      borderColor: 'divider', 
      overflow: 'auto', 
      bgcolor: '#000000',
      color: 'white'
    }}>
      <Typography variant="body2" sx={{ 
        fontWeight: 'bold', 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'rgba(255,255,255,0.1)',
        bgcolor: '#000000',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <FolderOpenIcon fontSize="small" sx={{ color: 'white' }} />
        Source Files
      </Typography>
      {Object.values(fileTree)
        .sort((a: any, b: any) => {
          // Folders first, then files
          if (a.isFile && !b.isFile) return 1;
          if (!a.isFile && b.isFile) return -1;
          // Within same type, sort alphabetically
          return a.name.localeCompare(b.name);
        })
        .map(node => renderTreeNode(node, '', 0))}
    </Box>
  );
};

// Source File Editor Component  
const SourceFileEditor = ({ filename, content, onContentChange, sourceBundle, modifiedFiles, onFileChange, activeTab, compileAndPreview, setCompiledApp }) => {
  console.log('✏️ SourceFileEditor render:', { filename, contentLength: content?.length });
  const [validationAnnotations, setValidationAnnotations] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const validationWorkerRef = useRef(null);
  
  const getEditorMode = (filename) => {
    if (!filename) return 'text';
    if (filename.endsWith('.tsx') || filename.endsWith('.jsx')) return 'typescript'; // Use TypeScript mode for better JSX support
    if (filename.endsWith('.ts')) return 'typescript';
    if (filename.endsWith('.js')) return 'javascript';
    if (filename.endsWith('.css') || filename.endsWith('.scss')) return 'css';
    if (filename.endsWith('.html')) return 'html';
    if (filename.endsWith('.json')) return 'json';
    return 'text';
  };

  const isModified = filename && modifiedFiles[filename];
  const originalContent = filename ? sourceBundle?.files[filename] || '' : '';
  
  // Check if file type supports validation
  const supportsValidation = filename && (
    filename.endsWith('.tsx') || 
    filename.endsWith('.jsx') || 
    filename.endsWith('.ts') || 
    filename.endsWith('.js')
  );

  const handleRevertFile = () => {
    if (!filename || !isModified) return;
    
    // Remove from modified files to revert to original
    const newModifiedFiles = { ...modifiedFiles };
    delete newModifiedFiles[filename];
    onFileChange(newModifiedFiles);
    
    // If we're currently viewing the preview, recompile immediately with the correct state
    if (activeTab === 'preview' && setCompiledApp) {
      console.log('🔄 File reverted, updating preview with reverted state...');
      
      // Immediately compile with the reverted state
      const finalFiles = Object.keys(newModifiedFiles).length > 0 
        ? { ...sourceBundle.files, ...newModifiedFiles }
        : sourceBundle.files;
      
      const bundleWithChanges = { ...sourceBundle, files: finalFiles };
      
      // Directly set the compiled app with the reverted changes
      setCompiledApp(bundleWithChanges);
    }
  };

  const handleRefreshFromServer = async () => {
    // TODO: Implement refresh from server for individual file
    // This would fetch the latest version of the file from the server
    console.log('🔄 Refresh from server for:', filename);
    // For now, just revert to original bundle content
    handleRevertFile();
  };

  const handleValidateFile = async () => {
    if (!filename || !supportsValidation || isValidating) return;
    
    setIsValidating(true);
    
    try {
      // Create worker if it doesn't exist
      if (!validationWorkerRef.current) {
        validationWorkerRef.current = new Worker(
          new URL('../../workers/typescript-validator.worker.ts', import.meta.url),
          { type: 'module' }
        );
        
        validationWorkerRef.current.onmessage = (e) => {
          const { ok, annotations } = e.data;
          console.log('🔍 Validation result:', { ok, annotations });
          setValidationAnnotations(annotations || []);
          setIsValidating(false);
        };
        
        validationWorkerRef.current.onerror = (error) => {
          console.error('❌ Validation worker error:', error);
          setIsValidating(false);
        };
      }
      
      // Send validation request
      validationWorkerRef.current.postMessage({
        code: content || '',
        filename: filename,
        compilerOptions: {}
      });
      
    } catch (error) {
      console.error('❌ Failed to start validation:', error);
      setIsValidating(false);
    }
  };

  // Cleanup worker on unmount
  React.useEffect(() => {
    return () => {
      if (validationWorkerRef.current) {
        validationWorkerRef.current.terminate();
      }
    };
  }, []);

  if (!filename) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Select a file from the tree to start editing
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* File Header with Controls */}
      <Box sx={{ 
        p: 1, 
        borderBottom: 1, 
        borderColor: 'rgba(255,255,255,0.1)', 
        bgcolor: '#1a202c',
        color: 'white',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
            {getFileIcon(filename)} {filename}
          </Typography>
          {isModified && (
            <Chip 
              label="Modified" 
              size="small" 
              sx={{ 
                bgcolor: '#ff9800', 
                color: 'white',
                height: '20px',
                fontSize: '10px'
              }} 
            />
          )}
        </Box>
        
        {/* File-level controls */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {supportsValidation && (
            <IconButton
              onClick={handleValidateFile}
              size="small"
              title={isValidating ? "Validating..." : "Validate TypeScript/JSX"}
              disabled={isValidating}
              sx={{ color: validationAnnotations.length > 0 ? '#f44336' : '#2196f3' }}
            >
              {isValidating ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                <ValidateIcon fontSize="small" />
              )}
            </IconButton>
          )}
          {isModified && (
            <>
              <IconButton
                onClick={handleRevertFile}
                size="small"
                title="Revert to Original"
                sx={{ color: '#ff9800' }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleRefreshFromServer}
                size="small"
                title="Refresh from Server"
                sx={{ color: '#4caf50' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      
      {/* Editor */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TemplateEditorAce
          value={content || ''}
          onChange={onContentChange}
          mode={getEditorMode(filename)}
          annotations={validationAnnotations}
        />
      </Box>
    </Box>
  );
};

// Helper function to build file tree structure
const buildFileTree = (files) => {
  const tree = {};
  
  Object.keys(files).forEach(filePath => {
    const parts = filePath.split('/');
    let current = tree;
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      
      if (isFile) {
        current[part] = {
          name: part,
          path: filePath,
          isFile: true
        };
      } else {
        if (!current[part]) {
          current[part] = {
            name: part,
            children: {},
            isFile: false
          };
        }
        current = current[part].children;
      }
    }
  });
  
  return tree;
};

// Helper function to get MUI file icon
const getFileIcon = (filename) => {
  if (filename.endsWith('.tsx') || filename.endsWith('.jsx')) return <CodeIcon fontSize="small" sx={{ color: '#61dafb' }} />; // React blue
  if (filename.endsWith('.ts')) return <CodeIcon fontSize="small" sx={{ color: '#3178c6' }} />; // TypeScript blue
  if (filename.endsWith('.js')) return <JavaScriptIcon fontSize="small" sx={{ color: '#f7df1e' }} />; // JavaScript yellow
  if (filename.endsWith('.css') || filename.endsWith('.scss')) return <CssIcon fontSize="small" sx={{ color: '#1572b6' }} />; // CSS blue
  if (filename.endsWith('.html')) return <HtmlIcon fontSize="small" sx={{ color: '#e34f26' }} />; // HTML orange
  if (filename.endsWith('.json')) return <SettingsIcon fontSize="small" sx={{ color: '#ffffff' }} />; // White for config
  if (filename.endsWith('.md')) return <DescriptionIcon fontSize="small" sx={{ color: '#ffffff' }} />; // White for docs
  if (filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.svg')) return <ImageIcon fontSize="small" sx={{ color: '#ffffff' }} />; // White for images
  return <FileIcon fontSize="small" sx={{ color: '#ffffff' }} />; // White for other files
};

// Hook to fetch derived agent
const useDerivedAgent = (applicationRecord: any) => {
  const [derivedAgent, setDerivedAgent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const dataProvider = useDataProvider();

  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchDerivedAgent = async () => {
      if (!applicationRecord?.key) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const entityKey = applicationRecord.key;

        // Fetch AI assistant derived from this application
        const { data } = await dataProvider.getList('aiassistant', {
          filter: {
            'derivedBy.entityKey': entityKey,
            _includeDerived: true  // Special flag to include derived entities in results
          },
          pagination: { page: 1, perPage: 10 },
          sort: { field: 'createdAt', order: 'DESC' }
        });

        if (data && data.length > 0) {
          console.log('✅ [useDerivedAgent] Found derived agent:', data[0]);
          setDerivedAgent(data[0]);
        } else {
          console.log('ℹ️ [useDerivedAgent] No derived agent found for application:', entityKey);
          setDerivedAgent(null);
        }
      } catch (err: any) {
        console.error('Error fetching derived agent:', err);
        setError(err.message || 'Failed to fetch derived agent');
      } finally {
        setLoading(false);
      }
    };

    fetchDerivedAgent();
  }, [applicationRecord?.id, applicationRecord?.key, dataProvider, refreshTrigger]);

  return { derivedAgent, loading, error, refetch };
};

// Context for sharing derived agent across tabs
const DerivedAgentContext = React.createContext<{
  derivedAgent: any;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} | null>(null);

// Agent Configuration Tab Component
const DerivedAgentConfigurationTab: React.FC = () => {
  const record: any = useRecordContext();
  const derivedAgentContext = React.useContext(DerivedAgentContext);
  const { derivedAgent, loading, error, refetch } = derivedAgentContext || { derivedAgent: null, loading: false, error: null, refetch: () => {} };
  const [instructions, setInstructions] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Initialize instructions when derived agent loads
  useEffect(() => {
    console.log('🔍 [Agent Config] Derived agent changed:', derivedAgent);
    console.log('🔍 [Agent Config] Instructions path check:', {
      'derivedAgent.instructions': derivedAgent?.instructions,
      'derivedAgent.configuration': derivedAgent?.configuration,
      'derivedAgent.configuration.instructions': derivedAgent?.configuration?.instructions
    });
    
    if (derivedAgent?.configuration?.instructions) {
      console.log('✅ [Agent Config] Setting instructions from configuration.instructions');
      setInstructions(derivedAgent.configuration.instructions);
      setHasChanges(false);
    } else if (derivedAgent?.instructions) {
      console.log('✅ [Agent Config] Setting instructions from instructions');
      setInstructions(derivedAgent.instructions);
      setHasChanges(false);
    }
  }, [derivedAgent]);

  const handleInstructionsChange = (value: string) => {
    setInstructions(value);
    setHasChanges(value !== (derivedAgent?.configuration?.instructions || derivedAgent?.instructions || ''));
  };

  const handleSaveInstructions = async () => {
    if (!derivedAgent || !hasChanges) return;

    setSaving(true);
    try {
      // Update the derived agent with modified instructions
      await dataProvider.update('aiassistant', {
        id: derivedAgent.id,
        data: {
          ...derivedAgent,
          configuration: {
            ...derivedAgent.configuration,
            instructions: instructions
          }
        },
        previousData: derivedAgent
      });

      notify('✅ Agent instructions saved successfully', { type: 'success' });
      setHasChanges(false);
      
      // Refetch the derived agent to show updated data
      refetch();
    } catch (err: any) {
      console.error('❌ Error saving agent instructions:', err);
      notify(`Failed to save instructions: ${err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading derived AI agent...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading derived agent: {error}
        </Alert>
      </Box>
    );
  }

  if (!derivedAgent) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No derived AI agent found yet. The agent will be automatically generated on the backend when needed.
        </Alert>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Application key: <strong>{record?.key}</strong>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#000000', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #333' }}>
        <Box>
          <Typography variant="body2" sx={{ color: '#4fc3f7' }}>
            <strong>Derived AI Agent:</strong> {derivedAgent.name || derivedAgent.assistantKey}
          </Typography>
          <Typography variant="caption" sx={{ color: '#b0bec5' }}>
            Configure the AI agent's system instructions and behavior
          </Typography>
        </Box>
        {hasChanges && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {saving && <CircularProgress size={20} />}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveInstructions}
              disabled={saving}
              label={saving ? 'Saving...' : 'Save Instructions'}
            />
          </Box>
        )}
      </Box>
      
      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes to the agent instructions. Click "Save Instructions" to persist your changes.
        </Alert>
      )}
      
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Instructions
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Define how the AI agent should behave and respond to user queries. These instructions guide the agent's personality, expertise, and response style.
          </Typography>
          
          <Box sx={{ mt: 2, minHeight: '500px', height: '60vh' }}>
            <TemplateEditorAce
              value={instructions}
              onChange={handleInstructionsChange}
              mode="text"
            />
          </Box>
          
          {derivedAgent.assistantKey && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#1a202c', borderRadius: 1, border: '1px solid #4a5568' }}>
              <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                <strong style={{ color: '#4fc3f7' }}>Agent Key:</strong> {derivedAgent.assistantKey}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const ApplicationEdit = (props) => {
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [originalRecord, setOriginalRecord] = useState(null);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const attributes = [
    { source: "name", label: "Name" },
    { source: "description", label: "Description" },
    { source: "executionProviderKey", label: "Implementation" }
  ];

  const getNewFunction = () => {
    return {
      name: "New",
      // generate a unique id without using a library for now
      id: Date.now().toString(36) + Math.random().toString(36).substring(2, 15),
    };
  };

  const handleExportApplication = (record: any) => {
    if (!record) return;

    // Convert Buffer data to base64 for export (browser-compatible, stack-safe)
    let compiledUIDataBase64 = null;
    if (record.compiledUIData) {
      try {
        // Handle different Buffer representations in browser
        if (typeof record.compiledUIData === 'string') {
          // Already base64 string
          compiledUIDataBase64 = record.compiledUIData;
          console.log('📦 UI data already as base64 string:', compiledUIDataBase64.length, 'characters');
        } else if (record.compiledUIData.type === 'Buffer' && Array.isArray(record.compiledUIData.data)) {
          // Handle serialized Buffer: {type: 'Buffer', data: [1, 2, 3, ...]}
          const uint8Array = new Uint8Array(record.compiledUIData.data);
          compiledUIDataBase64 = arrayBufferToBase64(uint8Array.buffer);
          console.log('📦 Converted serialized Buffer to base64:', compiledUIDataBase64.length, 'characters');
        } else if (record.compiledUIData instanceof ArrayBuffer) {
          compiledUIDataBase64 = arrayBufferToBase64(record.compiledUIData);
          console.log('📦 Converted ArrayBuffer to base64:', compiledUIDataBase64.length, 'characters');
        } else if (record.compiledUIData instanceof Uint8Array) {
          compiledUIDataBase64 = arrayBufferToBase64(record.compiledUIData.buffer);
          console.log('📦 Converted Uint8Array to base64:', compiledUIDataBase64.length, 'characters');
        } else {
          console.warn('⚠️ Unknown compiledUIData format:', typeof record.compiledUIData);
        }
      } catch (error) {
        console.error('❌ Failed to convert UI data to base64:', error);
      }
    }

    // Helper function to convert ArrayBuffer to base64 without stack overflow
    function arrayBufferToBase64(buffer: ArrayBuffer): string {
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunkSize = 8192; // Process in chunks to avoid stack overflow
      
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.slice(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
      }
      
      return btoa(binary);
    }

    // Create a clean export object
    const exportData = {
      name: record.name,
      key: record.key,
      description: record.description,
      configuration: record.configuration,
      openApiSpec: record.openApiSpec, // Include the base64 encoded OpenAPI spec
      sourceCodeStoreId: record.sourceCodeStoreId, // Include source code reference
      compiledUIDataBase64: compiledUIDataBase64, // Include compiled UI as base64
      
      // ===== VERSIONING AND PUBLISHING METADATA =====
      published: record.published,
      currentVersion: record.currentVersion,
      versionLabel: record.versionLabel,
      publishedAt: record.publishedAt,
      publishedBy: record.publishedBy,
      versions: record.versions, // Include full version history
      
      // Include any other fields you want to export
      exportMetadata: {
        exportedAt: new Date().toISOString(),
        exportedBy: "Admin Portal",
        version: "1.2", // Updated version to reflect new versioning support
        includesUIAssets: !!(compiledUIDataBase64 || record.sourceCodeStoreId),
        uiType: record.configuration?.uiType || 'none',
        includesVersionHistory: !!(record.versions && record.versions.length > 0),
        currentVersion: record.currentVersion || 1,
        isPublished: record.published || false
      }
    };

    // Create and download the file
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${record.name || 'application'}-export.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCompileUI = async (record: any) => {
    try {
      notify('Starting UI compilation...', { type: 'info' });
      
      // Call the UI compilation endpoint
      const result = await dataProvider.update('application', { 
        id: record.id, 
        data: record,
        previousData: record
      });

      if (result.data.success) {
        notify('✅ UI compiled successfully!', { type: 'success' });
        // Refresh the record to show updated compilation status
        window.location.reload();
      } else {
        notify(`❌ Compilation failed: ${result.data.message}`, { type: 'error' });
      }
    } catch (error) {
      console.error('UI compilation error:', error);
      notify('❌ UI compilation failed', { type: 'error' });
    }
  };

  const handleTriggerBuild = async (record: any) => {
    try {
      notify('Starting UI build process...', { type: 'info' });
      
      // Trigger build by updating with a build flag
      const result = await dataProvider.update('application', { 
        id: record.id, 
        data: {
          ...record,
          triggerBuild: true
        },
        previousData: record
      });

      notify('✅ UI build process started! Check logs for progress.', { type: 'success' });
      
      // Refresh after a delay to show updated compilation status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error('UI build error:', error);
      notify('❌ UI build failed to start', { type: 'error' });
    }
  };

  const EditActions = () => {
    const record = useRecordContext();

    const handleExportWithOpenApiSpec = async () => {
      try {
        notify('Preparing application export...', { type: 'info' });
        
        // Fetch the full application including all binary/large fields using the data provider
        const fullRecord = await dataProvider.getOneWithSelect('application', record.id, [
          'openApiSpec',
          'compiledUIData',
          'sourceCodeStoreId'
        ]);
        
        // Provide feedback about what's being exported
        const hasUIAssets = !!(fullRecord.compiledUIData || fullRecord.sourceCodeStoreId);
        const exportMessage = hasUIAssets 
          ? '✅ Application exported with UI assets included!'
          : '✅ Application exported (no UI assets found)';
          
        handleExportApplication(fullRecord);
        notify(exportMessage, { type: 'success' });
        
      } catch (error) {
        console.error('Error fetching full application for export:', error);
        notify('Failed to export application', { type: 'error' });
      }
    };

    return (
      <TopToolbar>
        <IconButton
          onClick={handleExportWithOpenApiSpec}
          title="Export Application"
          color="primary"
        >
          <DownloadIcon />
        </IconButton>
      </TopToolbar>
    );
  };


  // Transform function to ensure functions are always preserved and handle UI consolidation
  const transform = (data: any, { previousData }: any) => {
    console.log('🔍 Transform called with data:', {
      name: data.name,
      key: data.key,
      description: data.description,
      hasUI: data.configuration?.hasUI,
      uiType: data.configuration?.uiType,
      hasFunctions: !!data.configuration?.functions,
      functionsLength: data.configuration?.functions?.length,
      previousDataFunctionsLength: previousData?.configuration?.functions?.length,
      allDataKeys: Object.keys(data)
    });
    
    // Always ensure key is preserved
    if (!data.key && previousData?.key) {
      console.log('🔑 Preserving key from previousData:', previousData.key);
      data.key = previousData.key;
    }
    
    // If functions are missing but we have them in the previous data, preserve them
    if (!data.configuration?.functions || data.configuration.functions.length === 0) {
      if (previousData?.configuration?.functions?.length > 0) {
        console.log('🛡️ Preserving functions from previousData:', previousData.configuration.functions.length);
        data = {
          ...data,
          configuration: {
            ...data.configuration,
            functions: previousData.configuration.functions
          }
        };
      }
    }
    
    // Handle UI configuration consolidation
    if (data.configuration?.hasUI && data.configuration?.uiType !== 'none') {
      console.log('🎨 Processing UI configuration');
      
      // Ensure UI configuration is properly structured
      if (!data.configuration.buildConfig) {
        data.configuration.buildConfig = {
          framework: 'react',
          language: 'typescript',
          bundler: 'webpack',
          dependencies: {}
        };
      }
      
      // Set default serve root if not provided
      if (!data.configuration.uiServeRoot) {
        data.configuration.uiServeRoot = '/ui';
      }
    }
    
    console.log('✅ Transform complete:', {
      hasKey: !!data.key,
      functionsLength: data.configuration?.functions?.length || 0,
      hasUI: data.configuration?.hasUI,
      uiType: data.configuration?.uiType
    });
    
    return data;
  };

  return (
    <Edit {...props} sx={{ height: '130vh', '& .RaEdit-main': { height: '100%' } }} actions={<EditActions />} transform={transform}>
      <ApplicationFormWrapper />
    </Edit>
  );
};

// Wrapper to provide derived agent context to form tabs
const ApplicationFormWrapper = () => {
  const record = useRecordContext();
  const derivedAgentData = useDerivedAgent(record);
  
  const attributes = [
    { source: "name", label: "Name" },
    { source: "description", label: "Description" },
    { source: "executionProviderKey", label: "Implementation" }
  ];

  const getNewFunction = () => {
    return {
      name: "New",
      // generate a unique id without using a library for now
      id: Date.now().toString(36) + Math.random().toString(36).substring(2, 15),
    };
  };
  
  return (
    <DerivedAgentContext.Provider value={derivedAgentData}>
      <TabbedForm 
        tabs={<ScrollableTabbedFormTabs />}
        sx={{
          '& .RaTabbedForm-tabs': {
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiTabs-root': {
            minHeight: '48px',
          },
          '& .MuiTabs-flexContainer': {
            gap: '4px',
          },
          '& .MuiTab-root': {
            minHeight: '48px',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            padding: '12px 16px',
          },
          '& .MuiTabs-scrollButtons': {
            width: '40px',
          }
        }}
      >
        <FormTab label="General">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CompilationStatus />
            </Grid>
            <Grid item xs={12}>
              <TextInput source="name" label="Name" fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextInput source="key" label="Key" fullWidth disabled />
            </Grid>
            <Grid item xs={3}>
              <BooleanInput 
                source="configuration.hasUI" 
                label="Has UI Component" 
                helperText="Enable UI functionality for this application"
              />
            </Grid>
            <Grid item xs={3}>
              <BooleanInput 
                source="published" 
                label="Published" 
                helperText="Make this application available to end users"
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="description"
                label="Description"
                fullWidth
                multiline
              />
            </Grid>
            
            {/* Version Information */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#1a202c', color: '#e2e8f0', p: 2, border: '1px solid #2d3748' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#4fc3f7' }}>
                  Version Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#a0aec0' }}>Current Version</Typography>
                          <Typography variant="h6" sx={{ color: '#e2e8f0' }}>{formData?.currentVersion || 1}</Typography>
                        </Box>
                      )}
                    </FormDataConsumer>
                  </Grid>
                  <Grid item xs={3}>
                    <TextInput source="versionLabel" label="Version Label" helperText="e.g., v1.0.0, beta, stable" />
                  </Grid>
                  <Grid item xs={3}>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#a0aec0' }}>Total Versions</Typography>
                          <Typography variant="h6" sx={{ color: '#e2e8f0' }}>{formData?.versions?.length || 0}</Typography>
                        </Box>
                      )}
                    </FormDataConsumer>
                  </Grid>
                  <Grid item xs={3}>
                    <FormDataConsumer>
                      {({ formData }) => (
                        <Box>
                          <Typography variant="body2" sx={{ color: '#a0aec0' }}>Published At</Typography>
                          <Typography variant="body2" sx={{ color: '#cbd5e0' }}>
                            {formData?.publishedAt 
                              ? new Date(formData.publishedAt).toLocaleDateString() 
                              : 'Not published'}
                          </Typography>
                        </Box>
                      )}
                    </FormDataConsumer>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            
            <Grid item xs={6}>
              <BooleanInput source="configuration.requiresRegeneration" label="Rebuild?" options={{ color: 'primary' }} />
            </Grid>
            <Grid item xs={6}>
              <SelectInput 
                source="configuration.uiType" 
                label="UI Type"
                choices={[
                  { id: 'none', name: 'No UI' },
                  { id: 'source', name: 'Source Code (Development)' },
                  { id: 'compiled', name: 'Compiled Assets (Production)' },
                ]}
                defaultValue="none"
              />
            </Grid>
            
            {/* UI Configuration */}
            <FormDataConsumer>
              {({ formData }) => {
                const hasUI = formData?.configuration?.hasUI;
                
                if (!hasUI) return null;
                
                return (
                  <>
                    <Grid item xs={6}>
                      <TextInput 
                        source="configuration.uiServeRoot" 
                        label="UI Serve Path" 
                        defaultValue="/ui" 
                        helperText="Path where UI will be served (e.g., /ui, /app)"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <BooleanInput 
                        source="configuration.enableSPA" 
                        label="Single Page Application" 
                        defaultValue={true} 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        UI Type: {formData.configuration?.uiType || 'none'} | 
                        Serve Root: {formData.configuration?.uiServeRoot || '/ui'} |
                        Compiled: {formData.configuration?.isUICompiled ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                  </>
                );
              }}
            </FormDataConsumer>
          </Grid>
        </FormTab>
        <FormTab label="Configuration">
          <ConfigurationEdit />
        </FormTab>
        <FormTab label="Functions">
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              right: 0, 
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <MockConfigurationControl />
            </Box>
            <ArrayEditor
              cardLabel="Function"
              source="configuration.functions"
              attributes={attributes}
              getNewItem={getNewFunction}
              EditComponent={AIFunctionEdit}
            />
          </Box>
        </FormTab>

        <FormTab label="UI Assets">
          <Typography variant="h6" gutterBottom>
            Upload UI Assets
          </Typography>
          
          <FormDataConsumer>
            {({ formData }) => {
              const isCompiledMode = formData?.configuration?.uiType === 'compiled';
              const hasUI = formData?.configuration?.hasUI;
              
              if (!hasUI) {
                return (
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary">
                        Enable "Has UI Component" in the General tab to upload UI assets.
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }
              
              // Helper function to display file info
              const FileUploadInfo = ({ fileData, label }) => {
                if (!fileData?.rawFile) return null;
                
                const file = fileData.rawFile;
                const sizeInKB = Math.round(file.size / 1024);
                const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
                const displaySize = file.size > 1024 * 1024 ? `${sizeInMB} MB` : `${sizeInKB} KB`;
                
                return (
                  <Card sx={{ mt: 1, mb: 2, bgcolor: 'success.light', border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        📁 {label} uploaded:
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>{file.name}</strong> ({displaySize})
                      </Typography>
                    </CardContent>
                  </Card>
                );
              };
              
              return (
                <Box>
                  {!isCompiledMode ? (
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Upload a .zip file containing your UI source code (React, Vue, etc.). 
                        The system will automatically detect the framework and dependencies when you save.
                      </Typography>
                      <FileInput 
                        source="zipFile" 
                        label="UI Source Code (.zip)" 
                        accept=".zip" 
                        sx={{ mb: 2 }}
                      />
                      <FileUploadInfo fileData={formData?.zipFile} label="Source code" />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Supported: React, Vue, Angular, Vanilla JS projects with package.json
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Upload a .zip file containing your compiled UI assets (dist/build folder). 
                        This zip will be served directly by the static file server.
                      </Typography>
                      <FileInput 
                        source="compiledZipFile" 
                        label="Compiled UI Assets (.zip)" 
                        accept=".zip" 
                        sx={{ mb: 2 }}
                      />
                      <FileUploadInfo fileData={formData?.compiledZipFile} label="Compiled assets" />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Should contain index.html and all static assets (JS, CSS, images)
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            }}
          </FormDataConsumer>
        </FormTab>

        <FormTab label="UI Configuration">
          <Typography variant="h6" gutterBottom>
            Build Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <SelectInput 
                source="configuration.buildConfig.framework" 
                label="Framework"
                choices={[
                  { id: 'react', name: 'React' },
                  { id: 'vue', name: 'Vue.js' },
                  { id: 'angular', name: 'Angular' },
                  { id: 'vanilla', name: 'Vanilla JS' },
                ]}
                defaultValue="react"
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput 
                source="configuration.buildConfig.language" 
                label="Language"
                choices={[
                  { id: 'typescript', name: 'TypeScript' },
                  { id: 'javascript', name: 'JavaScript' },
                ]}
                defaultValue="typescript"
              />
            </Grid>
            <Grid item xs={4}>
              <SelectInput 
                source="configuration.buildConfig.bundler" 
                label="Bundler"
                choices={[
                  { id: 'webpack', name: 'Webpack' },
                  { id: 'vite', name: 'Vite' },
                  { id: 'rollup', name: 'Rollup' },
                ]}
                defaultValue="webpack"
              />
            </Grid>
          </Grid>

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Dependencies</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <CodeEditor
                source="configuration.buildConfig.dependencies"
                label="Package Dependencies (JSON)"
                language="json"
                defaultValue="{}"
              />
            </AccordionDetails>
          </Accordion>
        </FormTab>

        <FormTab label="Published">
          <AppPreviewFrame />
        </FormTab>

        <FormTab label="Preview">
          <LiveWASMPreview />
        </FormTab>

        <FormTab label="Agent Configuration">
          <DerivedAgentConfigurationTab />
        </FormTab>

        <FormTab label="App Agent">
          <ApplicationKeyWrapper>
            {(key) => <ChatInterface applicationKey={key} isDrawer={false} />}
          </ApplicationKeyWrapper>
        </FormTab>

        <FormTab label="Embed Agent">
          <ApplicationKeyWrapper>
            {(key) => <EmbedAgent item={{assistantKey:key}} />}
          </ApplicationKeyWrapper>
        </FormTab>
        <FormTab label="API">
          <ApplicationKeyWrapper>
            {(key) => (
              <ApplicationApiDocs
                applicationKey={key}
                title={`${key} API Documentation`}
                description="Test and explore the API endpoints for this application. Use the 'Copy Bearer Token' button to authenticate in Swagger."
              />
            )}
          </ApplicationKeyWrapper>
        </FormTab>
        
        <FormTab label="Version History">
          <FormDataConsumer>
            {({ formData }) => {
              const versions = formData?.versions || [];
              
              if (versions.length === 0) {
                return (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        No Version History
                      </Typography>
                      <Typography color="textSecondary">
                        Version snapshots will be created automatically when you make significant changes to this application.
                      </Typography>
                    </CardContent>
                  </Card>
                );
              }
              
              return (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Version History ({versions.length} versions)
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    Complete history of all configuration changes and published versions
                  </Typography>
                  
                  {versions.slice().reverse().map((version: any, index: number) => (
                    <Card key={version.id || index} sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6">
                                Version {version.versionNumber}
                                {version.versionLabel && ` (${version.versionLabel})`}
                              </Typography>
                              {version.isPublished && (
                                <Chip label="Published" color="success" size="small" />
                              )}
                              {index === 0 && (
                                <Chip label="Current" color="primary" size="small" />
                              )}
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {version.description}
                            </Typography>
                            {version.createdBy && (
                              <Typography variant="caption" color="textSecondary">
                                Created by: {version.createdBy}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="body2" color="textSecondary">
                                Created: {new Date(version.createdAt).toLocaleString()}
                              </Typography>
                              {version.publishedAt && (
                                <Typography variant="body2" color="textSecondary">
                                  Published: {new Date(version.publishedAt).toLocaleString()}
                                </Typography>
                              )}
                              {version.uiCompiled && (
                                <Chip label="UI Compiled" size="small" sx={{ mt: 1 }} />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                        
                        {version.configuration && (
                          <Accordion sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2">View Configuration Snapshot</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box sx={{ 
                                bgcolor: '#1e1e1e', 
                                color: '#d4d4d4', 
                                p: 2, 
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: '400px'
                              }}>
                                <pre style={{ margin: 0, fontSize: '12px' }}>
                                  {JSON.stringify(version.configuration, null, 2)}
                                </pre>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              );
            }}
          </FormDataConsumer>
        </FormTab>
      </TabbedForm>
    </DerivedAgentContext.Provider>
  );
};

export default ApplicationEdit;


/**
 * 
 * 
 * <FormTab label="Theme">
          <TextInput label="Regen" source="configuration.requiresRegeneration" />

          <TextInput label="Name" source="configuration.theme.name" />
          <TextInput
            label="Description"
            source="configuration.theme.description"
          />
          <ColorInput
            label="Primary Color"
            source="configuration.theme.primaryColor"
          />
          <ColorInput
            label="Secondary Color"
            source="configuration.theme.secondaryColor"
          />
          <ImageInput
            label="Login Image"
            source="configuration.theme.brandImages.loginBackground.source"
          />
          <ImageInput
            label="Logo Image"
            source="configuration.theme.brandImages.logo.source"
          />
        </FormTab>
        <FormTab label="Modules">
          <ArrayInput source="configuration.appModules">
            <SimpleFormIterator>
              <TextInput source="name" />
              <TextInput source="description" />

              <ArrayInput source="uiFeatures">
                <SimpleFormIterator>
                  <TextInput source="name" />
                  <TextInput source="description" />

                  <ArrayEditor
                    cardLabel="Form"
                    source="forms"
                    attributes={attributes}
                    getNewItem={getNewFunction}
                    EditComponent={AppFormEdit}
                  />
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
 */
